import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, Calendar, Clock } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { BlogArticle } from "@/components/BlogArticle";
import { getPost, POSTS } from "@/content/posts";

export const Route = createFileRoute("/blog/$slug")({
  loader: ({ params }) => {
    const post = getPost(params.slug);
    if (!post) throw notFound();
    return { post };
  },
  head: ({ params, loaderData }) => {
    const post = loaderData?.post ?? getPost(params.slug);
    if (!post) {
      return {
        meta: [{ title: "Post not found — SmartEssay" }],
      };
    }
    const url = `https://smartessay.ai/blog/${post.slug}`;
    return {
      meta: [
        { title: `${post.title} — SmartEssay` },
        { name: "description", content: post.description },
        { property: "og:title", content: post.title },
        { property: "og:description", content: post.description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { property: "article:published_time", content: post.publishedAt },
        { property: "article:author", content: post.author },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [{
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post.title,
          description: post.description,
          datePublished: post.publishedAt,
          author: { "@type": "Organization", name: post.author },
          publisher: {
            "@type": "Organization",
            name: "Tilt University",
            url: "https://tilt.university",
          },
          mainEntityOfPage: url,
        }),
      }],
    };
  },
  notFoundComponent: () => (
    <div className="min-h-screen bg-hero-gradient">
      <SiteHeader />
      <main className="mx-auto max-w-2xl px-6 py-24 text-center">
        <h1 className="font-display text-3xl font-semibold">Post not found</h1>
        <p className="mt-3 text-muted-foreground">That guide may have moved. Browse the rest of the blog.</p>
        <Link to="/blog" className="mt-6 inline-flex items-center text-primary hover:underline">
          <ArrowLeft className="mr-1.5 h-4 w-4" /> Back to all posts
        </Link>
      </main>
    </div>
  ),
  component: BlogPost,
});

function BlogPost() {
  const { post } = Route.useLoaderData();
  const others = POSTS.filter((p) => p.slug !== post.slug).slice(0, 2);

  return (
    <div className="min-h-screen bg-hero-gradient">
      <SiteHeader />
      <main className="mx-auto max-w-2xl px-6 pb-24">
        <Link to="/blog" className="mt-6 inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="mr-1.5 h-4 w-4" /> All guides
        </Link>

        <header className="mt-6">
          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" />
              {new Date(post.publishedAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
            </span>
            <span aria-hidden>·</span>
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              {post.readingMinutes} min read
            </span>
          </div>
          <h1 className="mt-3 font-display text-3xl font-semibold leading-tight tracking-tight sm:text-4xl">
            {post.title}
          </h1>
          <p className="mt-3 text-lg text-muted-foreground">{post.description}</p>
          <p className="mt-3 text-xs text-muted-foreground">By {post.author}</p>
        </header>

        <article className="mt-10">
          <BlogArticle blocks={post.body} />
        </article>

        {others.length > 0 && (
          <section className="mt-16 border-t border-border pt-10">
            <h2 className="font-display text-xl font-semibold">Keep reading</h2>
            <ul className="mt-4 grid gap-4 sm:grid-cols-2">
              {others.map((p) => (
                <li key={p.slug}>
                  <Link
                    to="/blog/$slug"
                    params={{ slug: p.slug }}
                    className="block rounded-xl border border-border bg-card p-4 transition hover:border-primary/40"
                  >
                    <p className="font-display text-base font-semibold text-foreground">{p.title}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{p.description.slice(0, 90)}…</p>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>
    </div>
  );
}
